import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, fromEvent, merge } from 'rxjs';
import { auditTime, filter } from 'rxjs/operators';
import { AuthService } from '../../features/auth/services/auth.service';

/**
 * Routes where the user holds an SSO session and so has something to be
 * logged out of. Unauthenticated screens (login, forgot, reset, setup,
 * unlock) stay off the list — the idle timer there would log nobody out of
 * anything and the "session expired" message would make no sense.
 */
const PROTECTED_ROUTES: readonly string[] = ['/labs', '/security/mfa'];

/** True inactivity before the user is signed out, in milliseconds. */
const IDLE_TIMEOUT_MS = 20 * 60 * 1000;

/** How often an activity burst can refresh the timer. Mirrors the Lab FE. */
const ACTIVITY_AUDIT_MS = 1000;

/** The notice the login screen shows when idle expiry sent them there. */
const SESSION_EXPIRED_REASON = 'session_expired';

/**
 * Sign the user out after a stretch with no real activity on the page.
 *
 * The browser-side counterpart to the server's 20-minute idle TTL: the
 * server already drops a session that has not been used for 20 minutes, but
 * nothing on the frontend noticed that until the next click, so a tab left
 * open overnight still looked signed in. This service closes that gap by
 * tracking the user's own events (mouse, keyboard, scroll, touch) and
 * signing them out when there have been none for 20 minutes.
 *
 * Only started on routes behind the session cookie — on the sign-in or
 * forgot-password screens there is nothing to log the user out of, and a
 * stray "session expired" notice there would be misleading. Starts and
 * stops itself from the router, so individual components do not have to
 * opt in or clean up.
 *
 * This is separate from the setup page's own watchdog (which polls the
 * server to notice the 1-hour absolute expiry). The watchdog's requests
 * reset the server's idle timer, so without this service a page that polls
 * would never see the 20-minute drop happen. Here the clock is the user's
 * activity, not the server's.
 */
@Injectable({ providedIn: 'root' })
export class SessionIdleService implements OnDestroy {
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private activitySub: Subscription | null = null;
  private readonly routeSub: Subscription;

  constructor(
    private readonly router: Router,
    private readonly auth: AuthService,
  ) {
    // Started lazily so the login page does not create listeners it will
    // never need. When navigation first lands on a protected route the
    // watcher begins; the moment it leaves, it stops.
    this.routeSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        if (this.isProtected(e.urlAfterRedirects)) {
          this.start();
        } else {
          this.stop();
        }
      });
  }

  ngOnDestroy(): void {
    this.stop();
    this.routeSub.unsubscribe();
  }

  /** Start, or restart, the watcher. Safe to call when already running. */
  private start(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    this.stop();

    // The events a real user produces by being at their desk. Audited so a
    // dragging mouse does not drown the host in work; one burst per second
    // is enough to say "still here".
    const activity$ = merge(
      fromEvent(window, 'mousemove'),
      fromEvent(window, 'mousedown'),
      fromEvent(window, 'keydown'),
      fromEvent(window, 'scroll'),
      fromEvent(window, 'touchstart'),
    ).pipe(auditTime(ACTIVITY_AUDIT_MS));

    this.activitySub = activity$.subscribe(() => this.resetTimer());
    this.resetTimer();
  }

  /** Stop the watcher and release every listener it holds. */
  private stop(): void {
    if (this.activitySub !== null) {
      this.activitySub.unsubscribe();
      this.activitySub = null;
    }
    this.clearTimer();
  }

  private resetTimer(): void {
    this.clearTimer();
    this.idleTimer = setTimeout(() => this.onIdleExpired(), IDLE_TIMEOUT_MS);
  }

  private clearTimer(): void {
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  /**
   * Sign out and send the user to the login screen.
   *
   * `logout` is called before navigating so the server-side session row is
   * revoked rather than merely abandoned — otherwise the cookie would be
   * cleared locally while the row sat in the database until its own
   * expiry, and the next browser to open on this machine would be let
   * back in. Errors on logout do not stop the redirect; the user must end
   * up on the sign-in screen either way or the whole point is lost.
   */
  private onIdleExpired(): void {
    this.stop();
    const goToLogin = () =>
      this.router.navigate(['/login'], {
        queryParams: { reason: SESSION_EXPIRED_REASON },
      });
    this.auth.logout().subscribe({ next: goToLogin, error: goToLogin });
  }

  /** Trailing slashes and query strings do not change whether a route is protected. */
  private isProtected(url: string): boolean {
    const path = url.split('?')[0].replace(/\/+$/, '') || '/';
    return PROTECTED_ROUTES.some((p) => path === p || path.startsWith(p + '/'));
  }
}
